import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  OrderMessage,
  OrderMessageList,
  PostOrderMessageInput,
  QuickReply,
} from '@capsico/contracts';
import { errorMessage, errorRef } from '../api/client';
import { fetchMessages, sendMessage } from '../api/tracking';
import { mergeMessage, replaceMessage } from './liveOrderMerge';
import { messagesKey } from './useLiveOrder';
import { useFormDraft } from './useFormDraft';
import { showToast } from './useToast';

const MAX_LENGTH = 500;
/** Optimistic (not yet saved) messages carry this id prefix. */
export const LOCAL_PREFIX = 'local-';
const isEmptyText = (value: string) => value.trim() === '';

/**
 * Customer ↔ rider chat for one order (W14 §6). Messages load once while chat is open and then
 * arrive live through the order socket (useLiveOrder merges them into the same cache entry).
 * Sending is optimistic: the bubble shows at once and is taken back if the server refuses.
 * The typed text is draft-saved per order (Keep / Discard), cleared once sent.
 */
export function useOrderChat(
  orderId: string,
  chatOpen: boolean,
  messages: { failed: string; quickReplies: Record<QuickReply, string> },
) {
  const client = useQueryClient();
  const key = messagesKey(orderId);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [text, setText] = useState('');
  const [seenAt, setSeenAt] = useState(0);

  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchMessages(orderId),
    enabled: orderId !== '' && (chatOpen || sheetOpen),
    staleTime: 60_000,
  });

  const draft = useFormDraft(`chat:${orderId}`, text, {
    enabled: sheetOpen && orderId !== '',
    isEmpty: isEmptyText,
    base: '',
  });

  const send = useMutation({
    mutationFn: (input: PostOrderMessageInput & { tempId: string }) =>
      sendMessage(orderId, { body: input.body, quickReply: input.quickReply }),
    onMutate: async (input) => {
      await client.cancelQueries({ queryKey: key, exact: true });
      const previous = client.getQueryData<OrderMessageList>(key);
      const optimistic: OrderMessage = {
        id: input.tempId,
        sender: 'customer',
        body: input.quickReply ? messages.quickReplies[input.quickReply] : (input.body ?? ''),
        quickReply: input.quickReply ?? null,
        at: new Date().toISOString(),
        mine: true,
      };
      client.setQueryData<OrderMessageList | undefined>(key, (current) =>
        mergeMessage(current, optimistic),
      );
      return { previous };
    },
    onSuccess: (saved, input) => {
      client.setQueryData<OrderMessageList | undefined>(key, (current) =>
        replaceMessage(current, input.tempId, saved),
      );
    },
    onError: (error, input, context) => {
      client.setQueryData<OrderMessageList | undefined>(key, (current) =>
        context?.previous ? context.previous : replaceMessage(current, input.tempId, null),
      );
      if (input.body) setText(input.body);
      showToast(errorMessage(error, messages.failed), { tone: 'negative', ref: errorRef(error) });
    },
  });

  const list = query.data?.messages ?? [];
  const unread = sheetOpen
    ? 0
    : list.filter((entry) => !entry.mine && Date.parse(entry.at) > seenAt).length;
  const trimmed = text.trim();

  return {
    loading: query.isLoading && (chatOpen || sheetOpen),
    failed: query.isError,
    retry: () => void query.refetch(),
    open: query.data?.open ?? chatOpen,
    messages: list,
    quickReplies: (query.data?.quickReplies ?? []).map((entry) => entry.key),
    unread,
    sheetOpen,
    openSheet: () => setSheetOpen(true),
    closeSheet: () => {
      setSheetOpen(false);
      setSeenAt(Date.now());
    },
    text,
    setText: (value: string) => setText(value.slice(0, MAX_LENGTH)),
    maxLength: MAX_LENGTH,
    canSend: trimmed.length > 0 && !send.isPending,
    sending: send.isPending,
    sendText: () => {
      if (!trimmed) return;
      send.mutate({ body: trimmed, tempId: `${LOCAL_PREFIX}${crypto.randomUUID()}` });
      setText('');
      draft.clear();
    },
    sendQuick: (reply: QuickReply) =>
      send.mutate({ quickReply: reply, tempId: `${LOCAL_PREFIX}${crypto.randomUUID()}` }),
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const value = draft.keep();
      if (typeof value === 'string') setText(value);
    },
    discardDraft: draft.discard,
  };
}
